import React, { Component } from 'react';

import logger from '../utils/logger';
import logError from '../utils/logError';
import { ErrorContext } from '../context/errorContext';

import ErrorFallback from './ErrorFallback/ErrorFallback';

class ErrorBoundary extends Component {
  // Allow this component to access ErrorContext via this.context
  static contextType = ErrorContext;

  constructor(props) {
    super(props);
    // Track whether an error occurred and store the error itself
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // When a child throws, mark the boundary as failed
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error safely
    logger.error(logError(error, { componentStack: errorInfo?.componentStack }));

    // Notify the global error handler if it's available.
    const { handleError } = this.context;
    if (handleError) {
      handleError(error);
    }

    // Call optional onError callback passed via props
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    const { resetKeys } = this.props;
    // If there's no error being shown, nothing to reset
    if (!this.state.hasError) {
      return;
    }

    // If resetKeys exist but haven't changed, do not reset
    if (
      Array.isArray(resetKeys) &&
      Array.isArray(prevProps.resetKeys) &&
      resetKeys.length === prevProps.resetKeys.length &&
      resetKeys.every((key, index) => Object.is(key, prevProps.resetKeys[index]))
    ) {
      return;
    }

    // If resetKeys change, reset the error boundary state
    if (resetKeys !== prevProps.resetKeys) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = () => {
    // Clear error state so children can re-render normally
    this.setState({ hasError: false, error: null });

    // Trigger optional onReset callback
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, error } = this.state;
    const FallbackComponent = this.props.fallback ?? ErrorFallback;

    // If an error occurred, render the fallback UI
    if (hasError) {
      return (
        <FallbackComponent
          error={error}
          onRetry={this.resetErrorBoundary}
          resetErrorBoundary={this.resetErrorBoundary}
        />
      );
    }
    // Otherwise, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
